/**
 * Size Chart Modal Loader
 * Uses Section Rendering API to load size chart content into modals
 */

class SizeChartModalLoader {
  constructor() {
    this.sectionId = null; // Will be auto-detected
    this.cacheKey = 'size-chart-html';
    this.init();
  }

  init() {
    // Auto-detect section ID first
    this.detectSectionId().then(() => {
      // Pre-fetch and cache the size chart content on page load
      this.preloadSizeChart();
    });

    // Listen for modal open events
    this.setupModalListeners();
  }

  /**
   * Auto-detect the section ID by fetching the sizing-chart page
   */
  async detectSectionId() {
    try {
      const baseUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${baseUrl}pages/sizing-chart`);

      if (!response.ok) {
        console.error('Could not fetch sizing-chart page');
        return;
      }

      const html = await response.text();

      // Look for the section ID in the HTML
      // Pattern: id="shopify-section-template--[numbers]__size_chart_enhanced_[id]"
      const match = html.match(/id="shopify-section-(template--[^"]*__size_chart_enhanced_[^"]*)"/);

      if (match && match[1]) {
        this.sectionId = match[1];
        console.log('Auto-detected section ID:', this.sectionId);
      } else {
        console.error('Could not auto-detect section ID from sizing-chart page');
      }
    } catch (error) {
      console.error('Error detecting section ID:', error);
    }
  }

  /**
   * Pre-fetch the size chart section and cache it
   */
  async preloadSizeChart() {
    // Check if already cached in this session
    const cached = sessionStorage.getItem(this.cacheKey);
    if (cached) {
      return; // Already cached
    }

    try {
      const html = await this.fetchSizeChartSection();
      sessionStorage.setItem(this.cacheKey, html);
    } catch (error) {
      console.warn('Failed to preload size chart:', error);
    }
  }

  /**
   * Fetch the size chart section using Section Rendering API
   * Uses locale-aware URL construction for international stores
   */
  async fetchSizeChartSection() {
    // Ensure section ID is detected
    if (!this.sectionId) {
      await this.detectSectionId();
    }

    if (!this.sectionId) {
      throw new Error('Section ID not detected. Make sure /pages/sizing-chart exists.');
    }

    // Use window.Shopify.routes.root for locale-aware URLs (best practice from Shopify docs)
    const baseUrl = window.Shopify?.routes?.root || '/';
    const url = `${baseUrl}pages/sizing-chart?section_id=${this.sectionId}`;

    const response = await fetch(url);

    // Handle 404 - section doesn't exist
    if (response.status === 404) {
      throw new Error(`Section not found: ${this.sectionId}`);
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const html = await response.text();

    // Validate we got actual HTML content
    if (!html || html.trim().length === 0) {
      throw new Error('Received empty response from Section Rendering API');
    }

    return html;
  }

  /**
   * Setup listeners for modal open events
   */
  setupModalListeners() {
    // Find size chart modal dialogs (not openers - they have their own handlers)
    const sizeChartModals = document.querySelectorAll('modal-dialog[id*="size_chart"]');

    sizeChartModals.forEach(modal => {
      // Listen for when the modal gets the 'open' attribute
      // This is set by the ModalOpener's show() method
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (modal.hasAttribute('open')) {
              // Modal is being opened
              this.loadSizeChartIntoModal(`#${modal.id}`);
            }
          }
        });
      });

      observer.observe(modal, {
        attributes: true,
        attributeFilter: ['open']
      });
    });
  }

  /**
   * Load size chart content into the specified modal
   */
  async loadSizeChartIntoModal(modalId) {
    const modal = document.querySelector(modalId);
    if (!modal) return;

    const contentContainer = modal.querySelector('.product-popup-modal__content-info');
    if (!contentContainer) return;

    // Check if already loaded
    if (contentContainer.hasAttribute('data-size-chart-loaded')) {
      return;
    }

    // Show loading state
    contentContainer.innerHTML = '<div style="text-align: center; padding: 3rem;"><p>Loading size chart...</p></div>';

    try {
      // Try to get from cache first
      let html = sessionStorage.getItem(this.cacheKey);

      // If not cached, fetch it
      if (!html) {
        html = await this.fetchSizeChartSection();
        sessionStorage.setItem(this.cacheKey, html);
      }

      // Insert the HTML
      contentContainer.innerHTML = html;
      contentContainer.setAttribute('data-size-chart-loaded', 'true');

      // Re-initialize the size chart JavaScript
      this.initializeSizeChartFeatures();

    } catch (error) {
      console.error('Failed to load size chart:', error);
      contentContainer.innerHTML = '<div style="text-align: center; padding: 3rem; color: #d9534f;"><p>Failed to load size chart. Please refresh the page and try again.</p></div>';
    }
  }

  /**
   * Initialize size chart features after content is loaded
   */
  initializeSizeChartFeatures() {
    // Wait for DOM to settle, then initialize tab and unit toggle functionality
    setTimeout(() => {
      this.setupModalTabSwitching();
      this.setupModalUnitToggle();
    }, 100);
  }

  /**
   * Setup tab switching within the modal
   */
  setupModalTabSwitching() {
    const modal = document.querySelector('.size_chart_modal');
    if (!modal) return;

    const tabs = modal.querySelectorAll('.size-chart-tab');
    const contents = modal.querySelectorAll('.size-chart-content');

    if (tabs.length === 0) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();

        // Remove active class from all tabs and contents
        tabs.forEach(t => t.classList.remove('active'));
        contents.forEach(c => c.classList.remove('active'));

        // Add active class to clicked tab
        tab.classList.add('active');

        // Show corresponding content
        const targetId = tab.getAttribute('data-tab');
        const targetContent = modal.querySelector(`#${targetId}`);

        if (targetContent) {
          targetContent.classList.add('active');

          // Toggle unit toggle visibility based on tab setting
          this.toggleUnitToggleVisibility(targetContent);
        }
      });
    });

    // Set initial unit toggle visibility
    const activeContent = modal.querySelector('.size-chart-content.active');
    if (activeContent) {
      this.toggleUnitToggleVisibility(activeContent);
    }
  }

  /**
   * Setup unit toggle within the modal
   */
  setupModalUnitToggle() {
    const modal = document.querySelector('.size_chart_modal');
    if (!modal) return;

    const unitBtns = modal.querySelectorAll('.unit-btn');
    const unitTables = modal.querySelectorAll('.unit-table');

    if (unitBtns.length === 0) return;

    unitBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        // Get selected unit
        const selectedUnit = btn.getAttribute('data-unit');

        // Update button states
        unitBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // Toggle table visibility
        unitTables.forEach(table => {
          const tableUnit = table.getAttribute('data-unit');
          if (tableUnit === selectedUnit) {
            table.style.display = 'block';
          } else {
            table.style.display = 'none';
          }
        });
      });
    });
  }

  /**
   * Toggle unit toggle visibility based on tab setting
   */
  toggleUnitToggleVisibility(tabContent) {
    const modal = document.querySelector('.size_chart_modal');
    if (!modal) return;

    const unitToggleWrapper = modal.querySelector('.unit-toggle-wrapper');
    if (!unitToggleWrapper) return;

    const hideUnitToggle = tabContent.getAttribute('data-hide-unit-toggle') === 'true';

    if (hideUnitToggle) {
      unitToggleWrapper.style.display = 'none';
    } else {
      unitToggleWrapper.style.display = 'flex';
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SizeChartModalLoader();
  });
} else {
  new SizeChartModalLoader();
}
